import React, { useMemo, useState, useCallback } from 'react'
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  Position,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { Copy, X } from 'lucide-react'

interface WorkflowRecord {
  seed_prompt: string
  style_preference?: string
  t2i_results?: string | any
  i2v_results?: string | any
  selected_card_id?: number
}

interface DirectorWorkflowTreeProps {
  record: WorkflowRecord
}

interface NodeDetailData {
  type: 'seed' | 't2i' | 'i2v'
  data: any
}

export const DirectorWorkflowTree: React.FC<DirectorWorkflowTreeProps> = ({ record }) => {
  const [selectedNodeDetail, setSelectedNodeDetail] = useState<NodeDetailData | null>(null)

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    const nodeId = node.id

    if (nodeId === 'seed') {
      setSelectedNodeDetail({
        type: 'seed',
        data: {
          seed_prompt: record.seed_prompt,
          style_preference: record.style_preference
        }
      })
    } else if (nodeId.startsWith('t2i-')) {
      let t2iData: any = null
      try {
        t2iData = typeof record.t2i_results === 'string'
          ? JSON.parse(record.t2i_results)
          : record.t2i_results
      } catch (e) {
        console.error('Failed to parse t2i_results:', e)
      }

      const cardId = parseInt(nodeId.replace('t2i-', ''))
      const card = t2iData?.results?.find((c: any) => c.id === cardId)

      if (card) {
        setSelectedNodeDetail({
          type: 't2i',
          data: card
        })
      }
    } else if (nodeId.startsWith('i2v-')) {
      let i2vData: any = null
      try {
        i2vData = record.i2v_results
          ? (typeof record.i2v_results === 'string'
              ? JSON.parse(record.i2v_results)
              : record.i2v_results)
          : null
      } catch (e) {
        console.error('Failed to parse i2v_results:', e)
      }

      const partKey = nodeId.replace('i2v-', '')
      const partData = i2vData?.storyboard?.[partKey]

      if (partData) {
        setSelectedNodeDetail({
          type: 'i2v',
          data: {
            key: partKey,
            ...partData
          }
        })
      }
    }
  }, [record])
  const { nodes, edges } = useMemo(() => {
    const nodes: Node[] = []
    const edges: Edge[] = []

    let t2iData: any = null
    let i2vData: any = null

    try {
      t2iData = typeof record.t2i_results === 'string'
        ? JSON.parse(record.t2i_results)
        : record.t2i_results
    } catch (e) {
      console.error('Failed to parse t2i_results:', e)
    }

    try {
      i2vData = record.i2v_results
        ? (typeof record.i2v_results === 'string'
            ? JSON.parse(record.i2v_results)
            : record.i2v_results)
        : null
    } catch (e) {
      console.error('Failed to parse i2v_results:', e)
    }

    // 根节点：种子需求
    nodes.push({
      id: 'seed',
      type: 'default',
      position: { x: 0, y: 300 },
      data: {
        label: (
          <div className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg">
            <div className="text-white font-bold mb-2">🌱 种子需求</div>
            <div className="text-white text-sm max-w-xs">{record.seed_prompt}</div>
            {record.style_preference && (
              <div className="text-blue-100 text-xs mt-2">风格: {record.style_preference}</div>
            )}
          </div>
        ),
      },
      sourcePosition: Position.Right,
    })

    // T2I 卡片节点
    if (t2iData?.results) {
      const cardCount = t2iData.results.length
      const cardSpacing = 150
      const startY = 300 - ((cardCount - 1) * cardSpacing) / 2

      t2iData.results.forEach((card: any, index: number) => {
        const isSelected = card.id === record.selected_card_id
        const nodeId = `t2i-${card.id}`

        nodes.push({
          id: nodeId,
          type: 'default',
          position: { x: 400, y: startY + index * cardSpacing },
          data: {
            label: (
              <div className={`p-3 rounded-lg shadow-lg ${
                isSelected
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 ring-2 ring-yellow-400'
                  : 'bg-gradient-to-r from-purple-400 to-purple-500'
              }`}>
                <div className="text-white font-bold text-sm mb-1">
                  {isSelected && '⭐ '}卡片 #{card.id}
                </div>
                <div className="text-white text-xs mb-1">{card.theme_name}</div>
                <div className="text-purple-100 text-xs">{card.style_tag}</div>
              </div>
            ),
          },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
        })

        // 连接种子到T2I卡片
        edges.push({
          id: `seed-${nodeId}`,
          source: 'seed',
          target: nodeId,
          animated: isSelected,
          style: { stroke: isSelected ? '#fbbf24' : '#9ca3af', strokeWidth: isSelected ? 3 : 2 },
        })
      })
    }

    // I2V 分镜节点
    if (i2vData?.storyboard && record.selected_card_id) {
      const selectedNodeId = `t2i-${record.selected_card_id}`
      const parts = [
        { key: 'part_1_0_to_10s', label: '0-10秒', y: 150 },
        { key: 'part_2_10_to_20s', label: '10-20秒', y: 300 },
        { key: 'part_3_20_to_30s', label: '20-30秒', y: 450 },
      ]

      parts.forEach((part) => {
        const partData = i2vData.storyboard[part.key]
        if (partData) {
          const nodeId = `i2v-${part.key}`

          nodes.push({
            id: nodeId,
            type: 'default',
            position: { x: 800, y: part.y },
            data: {
              label: (
                <div className="p-3 bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg">
                  <div className="text-white font-bold text-sm mb-1">🎬 {part.label}</div>
                  <div className="text-green-100 text-xs max-w-xs truncate">
                    {partData.action_summary}
                  </div>
                </div>
              ),
            },
            targetPosition: Position.Left,
          })

          edges.push({
            id: `${selectedNodeId}-${nodeId}`,
            source: selectedNodeId,
            target: nodeId,
            animated: true,
            style: { stroke: '#10b981', strokeWidth: 2 },
          })
        }
      })
    }

    return { nodes, edges }
  }, [record])

  return (
    <div style={{ width: '100%', height: '600px', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodeClick={onNodeClick}
        fitView
        attributionPosition="bottom-left"
      >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>

      {/* 节点详情面板 */}
      {selectedNodeDetail && (
        <div className="absolute top-4 right-4 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl p-4 max-w-md max-h-[500px] overflow-y-auto z-10">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-semibold">
              {selectedNodeDetail.type === 'seed' && '🌱 种子需求'}
              {selectedNodeDetail.type === 't2i' && '🎴 T2I 卡片详情'}
              {selectedNodeDetail.type === 'i2v' && '🎬 I2V 分镜详情'}
            </h4>
            <button
              onClick={() => setSelectedNodeDetail(null)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-3 text-sm">
            {/* 种子需求详情 */}
            {selectedNodeDetail.type === 'seed' && (
              <>
                <div>
                  <div className="text-gray-400 mb-1">种子需求:</div>
                  <div className="bg-gray-800 p-2 rounded border border-gray-700 text-gray-300">
                    {selectedNodeDetail.data.seed_prompt}
                  </div>
                  <button
                    onClick={() => copyToClipboard(selectedNodeDetail.data.seed_prompt)}
                    className="mt-2 flex items-center space-x-1 px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-white text-xs transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    <span>复制</span>
                  </button>
                </div>
                {selectedNodeDetail.data.style_preference && (
                  <div>
                    <div className="text-gray-400 mb-1">风格偏好:</div>
                    <div className="bg-gray-800 p-2 rounded border border-gray-700 text-gray-300">
                      {selectedNodeDetail.data.style_preference}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* T2I 卡片详情 */}
            {selectedNodeDetail.type === 't2i' && (
              <>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-400">卡片ID:</span>
                    <span className="text-white ml-2">#{selectedNodeDetail.data.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">画幅比例:</span>
                    <span className="text-white ml-2">{selectedNodeDetail.data.aspect_ratio}</span>
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">主题名:</div>
                  <div className="bg-gray-800 p-2 rounded border border-gray-700 text-gray-300">
                    {selectedNodeDetail.data.theme_name}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">风格标签:</div>
                  <div className="bg-gray-800 p-2 rounded border border-gray-700 text-gray-300">
                    {selectedNodeDetail.data.style_tag}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">完整提示词:</div>
                  <div className="bg-gray-800 p-2 rounded border border-gray-700 text-gray-300 text-xs max-h-40 overflow-y-auto">
                    {selectedNodeDetail.data.prompt}
                  </div>
                  <button
                    onClick={() => copyToClipboard(selectedNodeDetail.data.prompt)}
                    className="mt-2 flex items-center space-x-1 px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-white text-xs transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    <span>复制提示词</span>
                  </button>
                </div>
              </>
            )}

            {/* I2V 分镜详情 */}
            {selectedNodeDetail.type === 'i2v' && (
              <>
                <div>
                  <div className="text-gray-400 mb-1">时间段:</div>
                  <div className="bg-gray-800 p-2 rounded border border-gray-700 text-gray-300">
                    {selectedNodeDetail.data.key === 'part_1_0_to_10s' && '0-10秒'}
                    {selectedNodeDetail.data.key === 'part_2_10_to_20s' && '10-20秒'}
                    {selectedNodeDetail.data.key === 'part_3_20_to_30s' && '20-30秒'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">动作简述:</div>
                  <div className="bg-gray-800 p-2 rounded border border-gray-700 text-gray-300">
                    {selectedNodeDetail.data.action_summary}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">视频提示词:</div>
                  <div className="bg-gray-800 p-2 rounded border border-gray-700 text-gray-300 text-xs max-h-40 overflow-y-auto">
                    {selectedNodeDetail.data.video_prompt}
                  </div>
                  <button
                    onClick={() => copyToClipboard(selectedNodeDetail.data.video_prompt)}
                    className="mt-2 flex items-center space-x-1 px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-white text-xs transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    <span>复制视频提示词</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
